import { CheckCircle2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, endpoints } from '../services/api';
import { useSession } from '../store/session';
import { SignInForm } from './settings/AccountTab';
import { buttonClass } from './settings/controls';

const ERRORS: Record<string, string> = {
  invite_expired: 'This invite link has expired or was replaced. Ask for a new one.',
  group_full: 'This group is full.',
  restricted: 'This test site is private: your account isn’t on its list.',
};

/** /join?t=… – from a group invite link or QR code (Settings → Sharing). */
export function JoinPage() {
  const { load, loaded, user, config } = useSession();
  const token = new URLSearchParams(window.location.search).get('t') ?? '';
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [group, setGroup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = 'dark';
    void load();
  }, [load]);

  const join = async () => {
    setState('busy');
    setError(null);
    try {
      const r = await endpoints.joinGroup(token);
      setGroup(r.group.name);
      setState('done');
    } catch (e) {
      setError(e instanceof ApiError ? (ERRORS[e.code] ?? e.message) : 'That didn’t work – please try again.');
      setState('idle');
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-[var(--bg)]">
      <main className="mx-auto max-w-md px-6 py-12">
        <div className="mb-8 flex items-center gap-3">
          <Users className="h-7 w-7 text-[var(--accent)]" aria-hidden />
          <h1 className="text-2xl font-semibold">Join a group on KCL Canvas</h1>
        </div>
        {!token ? (
          <p className="muted">This link is incomplete. Ask the person who invited you to send it again.</p>
        ) : !loaded ? (
          <p className="muted">Loading…</p>
        ) : !config?.features.accounts ? (
          <p className="muted">Accounts aren’t switched on for this site yet.</p>
        ) : !user ? (
          <div className="space-y-4">
            <p>You’ve been invited to share canvases in a group. Sign in or create your free account to join.</p>
            <SignInForm returnTo={`/join?t=${encodeURIComponent(token)}`} />
          </div>
        ) : state === 'done' ? (
          <div className="space-y-4">
            <p className="flex items-center gap-2 text-lg">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" aria-hidden /> You’ve joined {group}.
            </p>
            <p className="muted">Canvases shared with the group appear in Settings → Sharing, and you can show them on any of your screens.</p>
            <a className={buttonClass} href="/">
              Open KCL Canvas
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            <p>
              Join the group as <strong>{user.email}</strong>? Its members will see your name and email, and you’ll be able to use canvases they share.
            </p>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button className={buttonClass} disabled={state === 'busy'} onClick={() => void join()}>
              Join the group
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
